import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, ChevronDown, Instagram, Mail } from "lucide-react";
import { LANGS, useI18n, ORG } from "@/lib/i18n";

/**
 * Mobile navigation drawer — CMS-driven (receives `items` from <Header/>).
 *
 * - Expandable accordions for `type: "dropdown"` entries. Opening one
 *   closes the other so the menu stays compact.
 * - Any selection or language/route change closes the entire menu.
 * - Keeps body-scroll lock + Escape to close.
 */
export default function MobileMenu({ open, onClose, items = [] }) {
  const { lang, setLang } = useI18n();
  const loc = useLocation();
  const [expanded, setExpanded] = useState(null);
  const T = (obj) => (obj && (obj[lang] || obj.en || obj.da)) || "";

  // Auto-close on route change.
  useEffect(() => { if (open) onClose(); }, [loc.pathname, loc.hash]); // eslint-disable-line

  useEffect(() => {
    if (!open) { setExpanded(null); return; }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const toggleGroup = (id) => setExpanded(prev => prev === id ? null : id);

  const closeAll = () => { setExpanded(null); onClose(); };
  // Language change should also close the entire menu per brief.
  const handleLang = (code) => { setLang(code); setExpanded(null); onClose(); };

  return (
    <div data-testid="mobile-menu"
         className="fixed inset-0 z-[60] lg:hidden overflow-y-auto overflow-x-hidden bg-brand-sand">
      <div className="min-h-screen flex flex-col px-6 pt-5 pb-10 max-w-full">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3">
          <Link to="/" onClick={closeAll} data-testid="menu-brand" aria-label="DANAmark home" className="min-w-0">
            <img src="/images/logo-full.webp" alt="DANAmark — Together across borders"
                 className="h-10 w-auto" />
          </Link>
          <button onClick={closeAll} data-testid="menu-close"
                  className="w-11 h-11 -mr-2 flex items-center justify-center text-brand-dark hover:text-brand-coral transition-colors shrink-0"
                  aria-label="Close menu">
            <X size={26} strokeWidth={1.5} />
          </button>
        </div>

        <nav className="mt-8 flex-1" aria-label="Mobile primary">
          <ul className="divide-y divide-brand-dark/10">
            {items.map(it => {
              if (it.type === "dropdown" && it.children?.length) {
                return (
                  <MobileGroup key={it.id} id={it.id} label={T(it.label)}
                               open={expanded === it.id}
                               onToggle={() => toggleGroup(it.id)}>
                    {it.children.map(c => (
                      <MobileSubLink key={c.id} to={c.url} onClose={closeAll} testid={`menu-${it.id}-${c.id}`}>
                        {T(c.label)}
                      </MobileSubLink>
                    ))}
                  </MobileGroup>
                );
              }
              // Highlighted controls (raffle, join) still render as prominent links.
              const emph = it.emphasis;
              return (
                <li key={it.id}>
                  <Link to={it.url} onClick={closeAll} data-testid={`menu-${it.id}`}
                        className={`block py-4 text-[22px] font-bold transition-colors ${emph === "raffle" ? "text-brand-coral" : emph === "primary" ? "text-brand-dark" : "text-brand-dark hover:text-brand-coral"}`}>
                    {T(it.label)}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Language + socials footer */}
        <footer className="mt-10 border-t border-brand-dark/15 pt-6 space-y-4">
          <div className="flex items-center gap-3 text-[13px] font-semibold" data-testid="menu-lang-selector">
            {LANGS.map((l, i) => (
              <React.Fragment key={l.code}>
                {i > 0 && <span className="text-brand-dark/25">·</span>}
                <button onClick={() => handleLang(l.code)} data-testid={`menu-lang-${l.code}`}
                        className={`transition-colors ${lang === l.code ? "text-brand-coral" : "text-brand-dark/50 hover:text-brand-dark"}`}>
                  {l.label}
                </button>
              </React.Fragment>
            ))}
          </div>
          <div className="flex flex-col gap-2 text-[13px]">
            <a href={ORG.instagramUrl} target="_blank" rel="noreferrer" data-testid="menu-instagram"
               className="inline-flex items-center gap-3 text-brand-dark/80 hover:text-brand-coral">
              <Instagram size={16} strokeWidth={1.5} /> {ORG.instagram}
            </a>
            <a href={`mailto:${ORG.email}`} data-testid="menu-email"
               className="inline-flex items-center gap-3 text-brand-dark/80 hover:text-brand-coral">
              <Mail size={16} strokeWidth={1.5} /> {ORG.email}
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
}

function MobileGroup({ id, label, open, onToggle, children }) {
  const panelId = `m-${id}-panel`;
  return (
    <li>
      <button type="button" onClick={onToggle}
              data-testid={`menu-${id}`}
              aria-expanded={open}
              aria-controls={panelId}
              className="w-full flex items-center justify-between py-4 text-[22px] font-bold text-brand-dark hover:text-brand-coral transition-colors">
        <span>{label}</span>
        <ChevronDown size={20} strokeWidth={2} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul id={panelId} className="pb-4 pl-2 space-y-1" data-testid={`menu-${id}-panel`}>
          {children}
        </ul>
      )}
    </li>
  );
}

function MobileSubLink({ to, onClose, testid, children }) {
  return (
    <li>
      <Link to={to} onClick={onClose} data-testid={testid}
            className="block py-2.5 text-[15px] text-brand-dark/80 hover:text-brand-coral transition-colors">
        {children}
      </Link>
    </li>
  );
}
